import { z } from 'zod';
import { CALENDARS, LOCALES, MILK_SESSIONS } from './enums';
import { hhmmToMinutes } from './dates';

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be HH:mm');

export const sessionSettingSchema = z.object({
  session: z.enum(MILK_SESSIONS),
  time: hhmm,
  name: z.string().trim().max(40).optional(),
});
export type SessionSetting = z.infer<typeof sessionSettingSchema>;

/** MR-2: 1–6 uniquely identified sessions with distinct times in chronological order. */
export const sessionsSchema = z
  .array(sessionSettingSchema)
  .min(1)
  .max(6)
  .superRefine((sessions, ctx) => {
    for (let i = 1; i < sessions.length; i++) {
      const prev = sessions[i - 1]!;
      const cur = sessions[i]!;
      if (hhmmToMinutes(cur.time) <= hhmmToMinutes(prev.time)) {
        ctx.addIssue({ code: 'custom', message: 'Times must be strictly increasing', path: [i, 'time'] });
      }
    }
    if (new Set(sessions.map((session) => session.session)).size !== sessions.length) {
      ctx.addIssue({ code: 'custom', message: 'Sessions must be unique' });
    }
  });

const int = (min: number, max: number, def: number) => z.number().int().min(min).max(max).default(def);
const num = (min: number, max: number, def: number) => z.number().min(min).max(max).default(def);

/** D-0. Stored in `tenants.settings`. Ranges from sprint 3 §S3-T12. */
export const tenantSettingsSchema = z
  .object({
    sessions: sessionsSchema.default([
      { session: 'MORNING', time: '06:00' },
      { session: 'EVENING', time: '17:00' },
    ]),
    lateEntryHours: int(1, 24, 6),
    managerEditWindowHours: int(1, 168, 24),
    calfMaxAgeMonths: int(6, 18, 12),
    gestationDays: int(270, 295, 283),
    dryPeriodDays: int(30, 90, 60),
    heatWatchStartDays: int(15, 30, 18),
    heatWatchEndDays: int(15, 30, 24),
    pregCheckDays: int(30, 120, 60),
    breedingStartDays: int(30, 120, 60),
    heiferBreedingAgeMonths: int(12, 30, 15),
    colostrumDays: int(2, 7, 4),
    milkDropThreshold: num(0.5, 0.95, 0.8),
    unaccountedMilkThreshold: num(0, 0.2, 0.02),
    enableHomeMilkUsage: z.boolean().default(false),
    receivableOverdueDays: int(1, 365, 30),
    defaultPeakLitersPerDay: num(1, 60, 12),
    firstLactationFactor: num(0.3, 1.5, 0.8),
    woodB: num(0.05, 0.6, 0.2),
    woodC: num(0.0005, 0.02, 0.004),
    defaultLocale: z.enum(LOCALES).default('am'),
    defaultCalendar: z.enum(CALENDARS).default('EC'),
  })
  .superRefine((s, ctx) => {
    if (s.heatWatchStartDays >= s.heatWatchEndDays) {
      ctx.addIssue({
        code: 'custom',
        message: 'heatWatchStartDays must be less than heatWatchEndDays',
        path: ['heatWatchStartDays'],
      });
    }
  });

export type TenantSettings = z.output<typeof tenantSettingsSchema>;
export type TenantSettingsInput = z.input<typeof tenantSettingsSchema>;

export const DEFAULT_SETTINGS: TenantSettings = tenantSettingsSchema.parse({});

/** Parse stored settings, filling any missing keys with D-0 defaults. */
export function parseSettings(raw: unknown): TenantSettings {
  const r = tenantSettingsSchema.safeParse(raw ?? {});
  return r.success ? r.data : DEFAULT_SETTINGS;
}
